// src/pages/ProductsDeletePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type Product = {
  id: number;
  code: string;
  name: string;
  price: number;
  wholesalePrice: number;
  stockOnHand: number;
  unit?: string;
  createdAt?: string;
  imageUrl?: string;
  thumbUrl?: string;
};

type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };

export default function ProductsDeletePage(): JSX.Element {
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(50);
  const [total, setTotal] = useState<number>(0);

  // preview รูปใหญ่
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // auto-hide toast
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  function extractCodeNumber(code: string): number | null {
    const m = String(code).match(/(\d+)(?!.*\d)/);
    return m ? Number(m[1]) : null;
  }

  const fetchProducts = async (query?: string, pageParam?: number) => {
    try {
      setLoading(true);
      setErrorMsg("");

      const pageToUse = pageParam ?? page;
      const qs: string[] = [];

      if (query && query.trim()) {
        qs.push(`q=${encodeURIComponent(query.trim())}`);
      }
      qs.push(`page=${pageToUse}`);
      qs.push(`limit=${limit}`);

      const url = apiUrl(`/productsListNew${qs.length ? "?" + qs.join("&") : ""}`);
      const resp = await fetch(url);
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

      const list: Product[] = (json.items ?? []).map((r: any) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        price: Number(r.price ?? 0),
        wholesalePrice: Number(r.wholesalePrice ?? 0),
        stockOnHand: Number(r.stockOnHand ?? 0),
        unit: r.unit ?? undefined,
        createdAt: r.createdAt ?? undefined,
        imageUrl: r.imageUrl ?? undefined,
        thumbUrl: r.thumbUrl ?? r.imageUrl ?? undefined,
      }));

      const sorted = [...list].sort((a, b) => {
        const na = extractCodeNumber(a.code);
        const nb = extractCodeNumber(b.code);
        if (na != null && nb != null && na !== nb) return na - nb;
        if (na != null && nb == null) return -1;
        if (na == null && nb != null) return 1;
        return a.code.localeCompare(b.code, "th-TH", { numeric: true, sensitivity: "base" });
      });

      setItems(sorted);
      setTotal(Number(json.total ?? 0));
      setPage(Number(json.page ?? pageToUse));
    } catch (err: any) {
      const msg = err?.message ?? "โหลดรายการสินค้าไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(undefined, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = () => {
    const qVal = qInput.trim();
    setQ(qVal);
    setPage(1);
    void fetchProducts(qVal || undefined, 1);
  };

  const refresh = () => {
    void fetchProducts(q || undefined, page);
  };

  const filtered = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const na = extractCodeNumber(a.code);
      const nb = extractCodeNumber(b.code);
      if (na !== null && nb !== null && na !== nb) return na - nb;
      return a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" });
    });
    return copy;
  }, [items]);

  // ----- ลบสินค้า -----
  const deleteProduct = async (code: string, name: string) => {
    // PMN-CRITICAL(/ProductsDeletePage): destructive product-master delete; frontend stock guard is UX only, backend DELETE remains the authority.
    const ok = window.confirm(`ยืนยันลบสินค้า "${name}" (${code}) ?\nรูปบน Azure จะถูกลบด้วย`);
    if (!ok) return;

    try {
      setLoading(true);
      const url = apiUrl(`/products/${encodeURIComponent(code)}`);
      const resp = await fetch(url, { method: "DELETE" });

      const ct = resp.headers.get("content-type") || "";
      const payload = ct.includes("application/json") ? await resp.json() : await resp.text();

      if (!resp.ok) {
        const errCode = typeof payload === "object" && payload?.error ? payload.error : "delete_failed";
        let friendly = "ลบสินค้าไม่สำเร็จ";
      
        if (errCode === "product_not_found") {
          friendly = "ไม่พบสินค้านี้ในระบบแล้ว";
        } else if (errCode === "product_in_use") {
          friendly = "ไม่สามารถลบได้ เพราะมีการใช้สินค้าในเอกสารอื่นแล้ว";
        } else if (errCode === "product_has_stock") {
          friendly = "ลบไม่ได้ เพราะยังมีสต็อกคงเหลือ ต้องตัดสต็อกให้เหลือ 0 ก่อน";
        } else if (errCode === "failed_to_delete_product") {
          friendly = "ลบสินค้าไม่สำเร็จ (เซิร์ฟเวอร์ผิดพลาด)";
        }
      
        throw new Error(friendly);
      }

      setToast({ show: true, message: `ลบสินค้า "${name}" สำเร็จ`, type: "success" });

      // reload list
      await fetchProducts(q || undefined, page);
    } catch (err: any) {
      const msg = err?.message ?? "ลบสินค้าไม่สำเร็จ";
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-danger">ลบสินค้า (Products Delete)</h4>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={refresh}>
                    รีเฟรช
                  </button>
                </div>

                {/* ค้นหา */}
                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    placeholder="ค้นหาโค้ด/ชื่อ/หน่วย"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  />
                  <button className="btn btn-primary" type="button" onClick={doSearch}>
                    ค้นหา
                  </button>
                </div>

                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดรายการสินค้า…</span>
                  </div>
                )}
                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    {errorMsg}
                  </div>
                )}

                {/* ตารางสินค้า */}
                <div className="border rounded-3 p-0 bg-white">
                  <div className="table-responsive" style={{ maxHeight: 520 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 76 }}>รูป</th>
                          <th style={{ width: 160 }}>โค้ด</th>
                          <th>ชื่อสินค้า</th>
                          <th style={{ width: 120 }} className="text-end">
                            คงเหลือ
                          </th>
                          <th style={{ width: 160 }} className="text-end">
                            ลบสินค้า
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 && !loading && (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-4">
                              ไม่พบรายการ
                            </td>
                          </tr>
                        )}

                        {filtered.map((p) => (
                          <tr key={p.id} className="user-select-none">
                            <td>
                              {(() => {
                                const thumbSrc = p.thumbUrl || p.imageUrl || null;
                                const fullSrc = p.imageUrl || thumbSrc || null;
                                return thumbSrc ? (
                                  <img
                                    src={thumbSrc}
                                    alt={p.name}
                                    width={56}
                                    height={56}
                                    loading="lazy"
                                    decoding="async"
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(fullSrc)}
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                );
                              })()}
                            </td>
                            <td className="fw-semibold">{p.code}</td>
                            <td>{p.name}</td>
                            <td className="text-end">{p.stockOnHand}</td>
                            <td className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={p.stockOnHand > 0}
                                    title={p.stockOnHand > 0 ? "ยังมีสต็อกคงเหลือ ลบไม่ได้" : ""}
                                    // PMN-CRITICAL(/ProductsDeletePage): keep this disabled state aligned with backend product_has_stock guard.
                                    onClick={() => void deleteProduct(p.code, p.name)}
                                    >
                                    ลบสินค้า
                                </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    แสดง {items.length} รายการ / ทั้งหมด {total} รายการ
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => {
                        const newPage = page - 1;
                        setPage(newPage);
                        void fetchProducts(q || undefined, newPage);
                      }}
                    >
                      ← ก่อนหน้า
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page * limit >= total || loading}
                      onClick={() => {
                        const newPage = page + 1;
                        setPage(newPage);
                        void fetchProducts(q || undefined, newPage);
                      }}
                    >
                      ถัดไป →
                    </button>
                  </div>
                </div>

                <a href="/" className="btn btn-link text-decoration-none flex-fill text-center mt-3">
                  ← กลับหน้าเมนู
                </a>
              </div>
            </div>

            {/* Preview รูปใหญ่ */}
            {previewImage && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000, pointerEvents: "none" }}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 12,
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                    background: "#fff",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
